import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Star, Link2, Globe2, BarChart3, Sparkles, ArrowRight, Check, Compass, Hotel, UtensilsCrossed, Wrench, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { api, formatApiError } from "../api";
import { toast } from "sonner";

const HERO_IMG =
  "https://images.unsplash.com/photo-1566371486490-560ded23b5e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHRvdXJpc3RzJTIwdmFjYXRpb258ZW58MHx8fHwxNzg3MjA2MjIwfDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FFFDF9] bg-grain">
      {/* Nav */}
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2" data-testid="brand-logo">
          <div className="w-9 h-9 rounded-lg bg-[#0A2540] grid place-items-center text-white font-bold font-display">R</div>
          <span className="font-display font-semibold text-lg tracking-tight">ReviewFlow</span>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          {user && user !== false ? (
            <Button
              data-testid="nav-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="rounded-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white"
            >
              {t("nav_dashboard")} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-slate-700 hover:text-slate-900 hidden sm:inline" data-testid="nav-login-link">
                {t("nav_login")}
              </Link>
              <Button
                data-testid="nav-signup-btn"
                onClick={() => navigate("/register")}
                className="rounded-full bg-[#E57C4F] hover:bg-[#E57C4F]/90 text-white"
              >
                {t("nav_signup")}
              </Button>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 pb-24 grid md:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="md:col-span-6"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2B8C9F]/10 text-[#2B8C9F] text-xs font-semibold mb-6" data-testid="hero-badge">
            <Sparkles className="w-3.5 h-3.5" /> {t("hero_badge")}
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A2540]">
            {t("hero_title_1")}<br />
            <span className="text-[#E57C4F]">{t("hero_title_2")}</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-lg">{t("hero_desc")}</p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button
              data-testid="hero-cta-btn"
              size="lg"
              onClick={() => navigate(user ? "/dashboard" : "/register")}
              className="rounded-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white h-12 px-8 text-base"
            >
              {t("hero_cta")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <div className="flex items-center gap-1 text-slate-500 text-sm">
              <Star className="w-4 h-4 fill-[#E57C4F] text-[#E57C4F]" />
              <Star className="w-4 h-4 fill-[#E57C4F] text-[#E57C4F]" />
              <Star className="w-4 h-4 fill-[#E57C4F] text-[#E57C4F]" />
              <Star className="w-4 h-4 fill-[#E57C4F] text-[#E57C4F]" />
              <Star className="w-4 h-4 fill-[#E57C4F] text-[#E57C4F]" />
              <span className="ml-2">{t("hero_trial_note")}</span>
            </div>
          </div>

          {/* features */}
          <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { icon: Link2, label: t("feat_unique_link") },
              { icon: Globe2, label: t("feat_5_lang") },
              { icon: BarChart3, label: t("feat_analytics") },
            ].map((f, i) => (
              <div key={i} className="text-sm">
                <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 grid place-items-center mb-2">
                  <f.icon className="w-4.5 h-4.5 text-[#2B8C9F]" />
                </div>
                <div className="font-medium text-slate-700 leading-tight">{f.label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="md:col-span-6 relative"
        >
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <img src={HERO_IMG} alt="Happy tourists" className="w-full h-[560px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/50 via-transparent to-transparent" />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="absolute top-6 left-6 glass rounded-2xl px-4 py-3 shadow-lg flex items-center gap-3"
            >
              <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
              <span className="text-xs font-semibold text-slate-800">{t("hero_card_today")}</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="absolute bottom-6 right-6 glass rounded-2xl p-4 shadow-lg w-64"
            >
              <div className="text-xs text-slate-500 mb-1">Tour Amalfi Sunset</div>
              <div className="font-display font-bold text-2xl text-[#0A2540]">92%</div>
              <div className="text-xs text-slate-600">{t("hero_card_completion")}</div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* For whom */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 border-t border-slate-200/60" data-testid="for-whom-section">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-semibold tracking-widest uppercase text-[#2B8C9F]">{t("for_whom_kicker")}</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#0A2540] tracking-tight">
            {t("for_whom_title")}
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Compass, label: t("for_whom_tour"), desc: t("for_whom_tour_desc") },
            { icon: Hotel, label: t("for_whom_hotel"), desc: t("for_whom_hotel_desc") },
            { icon: UtensilsCrossed, label: t("for_whom_rest"), desc: t("for_whom_rest_desc") },
            { icon: Wrench, label: t("for_whom_serv"), desc: t("for_whom_serv_desc") },
          ].map((it, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 hover:-translate-y-0.5 transition-transform" data-testid={`for-whom-${i}`}>
              <div className="w-11 h-11 rounded-lg bg-[#E57C4F]/10 grid place-items-center">
                <it.icon className="w-5 h-5 text-[#E57C4F]" />
              </div>
              <div className="mt-4 font-display font-semibold text-[#0A2540]">{it.label}</div>
              <div className="text-xs text-slate-500 mt-1">{it.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <Pricing user={user} />

      <footer className="max-w-7xl mx-auto px-6 lg:px-10 py-8 text-sm text-slate-500 border-t border-slate-200">
        © {new Date().getFullYear()} ReviewFlow. {t("footer_line")}
      </footer>
    </div>
  );
}

function Pricing({ user }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("monthly");
  const [loadingKey, setLoadingKey] = useState(null);
  const base = [
    { key: "starter", nameKey: "plan_starter", descKey: "plan_starter_desc", platforms: 2, credits: 500, monthly: 20, highlight: false },
    { key: "pro", nameKey: "plan_pro", descKey: "plan_pro_desc", platforms: 4, credits: 1200, monthly: 35, highlight: true },
  ];
  const cycles = [
    { key: "monthly", labelKey: "cycle_monthly", months: 1, discount: 0 },
    { key: "semiannual", labelKey: "cycle_semiannual", months: 6, discount: 0 },
    { key: "annual", labelKey: "cycle_annual", months: 12, discount: 0.1, badge: "−10%" },
  ];
  const current = cycles.find((c) => c.key === cycle);
  const computed = base.map((p) => {
    const totalNoDiscount = p.monthly * current.months;
    const total = totalNoDiscount * (1 - current.discount);
    const perMonth = total / current.months;
    return { ...p, total, perMonth, totalNoDiscount };
  });

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 border-t border-slate-200/60" data-testid="pricing-section" id="pricing">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="text-xs font-semibold tracking-widest uppercase text-[#2B8C9F]">{t("pricing_kicker")}</div>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#0A2540] tracking-tight">{t("pricing_title")}</h2>
        <p className="mt-3 text-slate-600">{t("pricing_subtitle")}</p>
      </div>

      <div className="flex justify-center mb-10">
        <div className="inline-flex bg-white border border-slate-200 rounded-full p-1" data-testid="cycle-toggle">
          {cycles.map((c) => (
            <button
              key={c.key}
              onClick={() => setCycle(c.key)}
              data-testid={`cycle-${c.key}`}
              className={`relative px-5 py-2 rounded-full text-sm font-semibold transition-colors ${cycle === c.key ? "bg-[#0A2540] text-white" : "text-slate-600 hover:text-slate-900"}`}
            >
              {t(c.labelKey)}
              {c.badge && (
                <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${cycle === c.key ? "bg-[#E57C4F] text-white" : "bg-[#E57C4F]/10 text-[#E57C4F]"}`}>
                  {c.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {computed.map((p) => (
          <div
            key={p.key}
            data-testid={`pricing-${p.key}`}
            className={`relative rounded-2xl p-6 flex flex-col ${p.highlight ? "bg-[#0A2540] text-white border border-[#0A2540]" : "bg-white text-slate-900 border border-slate-200"}`}
          >
            {p.highlight && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E57C4F] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                {t("plan_popular")}
              </div>
            )}
            <div className="font-display font-semibold text-xl">{t(p.nameKey)}</div>
            <div className={`text-xs mt-1 ${p.highlight ? "text-white/70" : "text-slate-500"}`}>{t(p.descKey)}</div>

            <div className="mt-6 flex items-baseline gap-1">
              <span className="font-display font-bold text-4xl">${p.perMonth.toFixed(p.perMonth % 1 === 0 ? 0 : 2)}</span>
              <span className={`text-sm ${p.highlight ? "text-white/70" : "text-slate-500"}`}>{t("per_month")}</span>
            </div>

            {current.discount > 0 ? (
              <div className={`text-xs mt-1 ${p.highlight ? "text-white/70" : "text-slate-500"}`}>
                <span className="line-through mr-1">${p.totalNoDiscount.toFixed(0)}</span>
                <span className="font-semibold text-[#E57C4F]">${p.total.toFixed(2)}</span> {t("billed_annually")}
              </div>
            ) : current.months > 1 ? (
              <div className={`text-xs mt-1 ${p.highlight ? "text-white/70" : "text-slate-500"}`}>
                ${p.total.toFixed(0)} {t("billed_every_months", { n: current.months })}
              </div>
            ) : (
              <div className={`text-xs mt-1 ${p.highlight ? "text-white/70" : "text-slate-500"}`}>{t("billed_monthly")}</div>
            )}

            <ul className={`mt-6 space-y-2.5 text-sm flex-1 ${p.highlight ? "text-white/90" : "text-slate-700"}`}>
              <li className="flex gap-2"><Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-[#E57C4F]" : "text-[#10B981]"}`} /> {t("plan_feat_platforms", { n: p.platforms })}</li>
              <li className="flex gap-2"><Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-[#E57C4F]" : "text-[#10B981]"}`} /> <strong className="font-semibold">{t("plan_feat_credits", { n: p.credits })}</strong></li>
              <li className="flex gap-2"><Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-[#E57C4F]" : "text-[#10B981]"}`} /> {t("plan_feat_photos")}</li>
              <li className="flex gap-2"><Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-[#E57C4F]" : "text-[#10B981]"}`} /> {t("plan_feat_tours")}</li>
              <li className="flex gap-2"><Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-[#E57C4F]" : "text-[#10B981]"}`} /> {t("plan_feat_multilang")}</li>
              <li className="flex gap-2"><Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-[#E57C4F]" : "text-[#10B981]"}`} /> {t("plan_feat_smart")}</li>
              <li className="flex gap-2"><Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-[#E57C4F]" : "text-[#10B981]"}`} /> {t("plan_feat_analytics")}</li>
              {p.key === "pro" && <li className="flex gap-2"><Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-[#E57C4F]" : "text-[#10B981]"}`} /> {t("plan_feat_priority")}</li>}
            </ul>

            <Button
              onClick={async () => {
                if (!user || user === false) {
                  // save intent then send to register
                  try { localStorage.setItem("rf_intended_lookup_key", `${p.key}_${cycle}`); } catch (e) {}
                  navigate("/register");
                  return;
                }
                setLoadingKey(p.key);
                try {
                  const { data } = await api.post("/payments/checkout", {
                    lookup_key: `${p.key}_${cycle}`,
                    origin_url: window.location.origin,
                  });
                  window.location.href = data.checkout_url;
                } catch (e) {
                  toast.error(formatApiError(e));
                  setLoadingKey(null);
                }
              }}
              disabled={loadingKey === p.key}
              data-testid={`pricing-cta-${p.key}`}
              className={`mt-6 rounded-full h-11 w-full font-semibold ${p.highlight ? "bg-[#E57C4F] hover:bg-[#E57C4F]/90 text-white" : "bg-[#0A2540] hover:bg-[#0A2540]/90 text-white"}`}
            >
              {loadingKey === p.key ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> …</>
              ) : (
                user ? t("plan_cta_start") : t("plan_cta_start")
              )}
            </Button>
          </div>
        ))}
      </div>

      <p className="text-center mt-8 text-xs text-slate-500">{t("pricing_footer")}</p>
    </section>
  );
}
